import Footer from '@/components/Footer';
import Header from '@/components/Header';
import { fadeIn, slideUp } from '@/utils/animations';
import { motion } from 'framer-motion';
import { Eye, FileText, Lock, Shield } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-green-50">
      <Header />
      <motion.main
        className="flex-1"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <div className="container mx-auto px-4 py-12">
          <motion.h1
            className="text-4xl font-bold text-green-800 mb-8 text-center"
            variants={slideUp}
          >
            Privacy Policy
          </motion.h1>

          {/* Introduction */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <Shield className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">Introduction</h2>
            </div>
            <p className="text-gray-600 mb-4">
              At PermitTree, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our permit management system. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
            </p>
            <p className="text-gray-600">
              Last updated: February 15, 2024
            </p>
          </motion.section>

          {/* Information Collection */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <FileText className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">Information We Collect</h2>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-green-700">Personal Information</h3>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>Full name and contact information</li>
                <li>Government-issued identification numbers</li>
                <li>Email address and phone number</li>
                <li>Property information and addresses</li>
                <li>Payment information</li>
                <li>Application history and permit records</li>
              </ul>

              <h3 className="text-xl font-semibold text-green-700 mt-6">Usage Data</h3>
              <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
                <li>IP address and browser information</li>
                <li>Device information</li>
                <li>Access times and dates</li>
                <li>Pages viewed and features used</li>
                <li>Error logs and performance data</li>
              </ul>
            </div>
          </motion.section>

          {/* How We Use Your Information */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <Eye className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">How We Use Your Information</h2>
            </div>
            <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
              <li>Process and manage permit applications</li>
              <li>Communicate about application status and updates</li>
              <li>Verify identity and maintain security</li>
              <li>Improve our services and user experience</li>
              <li>Comply with legal obligations</li>
              <li>Send important notifications and updates</li>
              <li>Generate anonymized statistical data</li>
            </ul>
          </motion.section>

          {/* Data Protection */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <div className="flex items-center mb-6">
              <Lock className="h-8 w-8 text-green-600 mr-4" />
              <h2 className="text-2xl font-semibold text-green-800">Data Protection</h2>
            </div>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication measures</li>
              <li>Secure data backup and recovery procedures</li>
              <li>Staff training on data protection and security</li>
            </ul>
          </motion.section>

          {/* Your Rights */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <h2 className="text-2xl font-semibold text-green-800 mb-6">Your Rights</h2>
            <p className="text-gray-600 mb-4">
              Under the Data Privacy Act of 2012 (Republic Act No. 10173), you have the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-600 ml-4 space-y-2">
              <li>Right to be informed about how your data is being processed</li>
              <li>Right to access your personal information</li>
              <li>Right to correct inaccurate or incomplete data</li>
              <li>Right to object to the processing of your personal data</li>
              <li>Right to erasure or blocking of your personal data</li>
              <li>Right to file a complaint regarding data privacy concerns</li>
              <li>Right to data portability</li>
            </ul>
          </motion.section>

          {/* Contact Information */}
          <motion.section className="mb-12 bg-white p-8 rounded-lg shadow-md" variants={fadeIn}>
            <h2 className="text-2xl font-semibold text-green-800 mb-6">Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="text-gray-600">
              <p>Email: privacy@permittree.com</p>
              <p>Phone: (123) 456-7890</p>
              <p>Address: DENR-PENRO Office, Boac, Marinduque</p>
            </div>
          </motion.section>
        </div>
      </motion.main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;